import React, { useRef } from "react";

import styles from "./BookingTabs.module.scss";

export type BookingTab = "coaches" | "dates" | "facility-calendar";

interface Props {
  tabs: Array<{ id: BookingTab; label: string }>;
  active: BookingTab;
  onSelect: (tab: BookingTab) => void;
}

/**
 * The pill tab bar above the facility booking content ("Coaches | Dates |
 * Facility Calendar"). Geometry follows the frame's Chip component: 40px tall,
 * 10/16 padding, 20px radius, 12px gap, orange when on.
 *
 * Renders nothing below two tabs — a single pill is a control that cannot do
 * anything, so a phase that ships one view shows no bar at all.
 */
const BookingTabs: React.FC<Props> = ({ tabs, active, onSelect }) => {
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);

  if (tabs.length < 2) {
    return null;
  }

  const activeIndex = tabs.findIndex((tab) => tab.id === active);

  /**
   * The keyboard half of `role="tab"`. Claiming the role tells assistive tech
   * that this row behaves a certain way, and it did not: every pill was its own
   * tab stop and the arrows did nothing, so a screen-reader user was told to
   * arrow through a bar that would not answer.
   *
   * Focus moves, and only focus — the pill is a real `<button>`, so Enter and
   * Space already select through `onClick`. Choosing on arrival would be the
   * other allowed reading, but each tab here fetches, and arrowing past one is
   * not a request to load it.
   */
  const moveFocus = (event: React.KeyboardEvent, from: number) => {
    const last = tabs.length - 1;
    let to: number | null = null;

    if (event.key === "ArrowRight") {
      to = from === last ? 0 : from + 1;
    } else if (event.key === "ArrowLeft") {
      to = from === 0 ? last : from - 1;
    } else if (event.key === "Home") {
      to = 0;
    } else if (event.key === "End") {
      to = last;
    }

    if (to === null) {
      return;
    }

    // Home and End scroll the page otherwise, and the arrows scroll it sideways.
    event.preventDefault();
    buttons.current[to]?.focus();
  };

  return (
    <div className={styles.tab_bar} role="tablist">
      {tabs.map((tab, index) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          ref={(element) => {
            buttons.current[index] = element;
          }}
          aria-selected={tab.id === active}
          /**
           * One tab stop for the whole row, on the selected pill — the arrows
           * are how you get to the others. Falls to the first pill when the
           * active tab is not among these, so the row can never become
           * unreachable by Tab.
           */
          tabIndex={index === (activeIndex === -1 ? 0 : activeIndex) ? 0 : -1}
          className={tab.id === active ? styles.tab_on : styles.tab_off}
          onClick={() => onSelect(tab.id)}
          onKeyDown={(event) => moveFocus(event, index)}
          data-testid={`booking-tab-${tab.id}`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
};

export default BookingTabs;
